import {Component, computed, inject, OnInit, DestroyRef, ChangeDetectionStrategy} from "@angular/core"
import {toSignal, takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {MatDialog} from "@angular/material/dialog"
import {MatTableModule} from "@angular/material/table"
import {MatButtonModule} from "@angular/material/button"
import {MatIconModule} from "@angular/material/icon"
import {InfiniteScrollAnchorComponent, OverflowableTextComponent} from "@common/components/data"
import {TableCellComponent} from "@common/components/tables/table-cell/table-cell.component"
import {TypedMatCellDefDirective} from "@common/directives"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {keepLoadingMore} from "@common/helpers/data/keep-loading"
import {BreakpointsService} from "@common/services/breakpoints/breakpoints.service"
import {ContentTypeModel, InvocationMethod} from "@generated"
import {BaseTableComponent} from "@platform/components/base/base-table/base-table.component"
import {EditTaskTypeDialogComponent, EditTaskTypeDialogData} from "@platform/components/task-types/edit-task-type-dialog/edit-task-type-dialog.component"
import {
    TaskTypesTableCreateTaskTypeGQL,
    TaskTypesTableDeleteTaskTypeGQL,
    TaskTypesTableItemFragment,
    TaskTypesTableItemsGQL,
    TaskTypesTableUpdateTaskTypeGQL,
} from "@platform/components/task-types/task-types-table/task-types-table.generated"
import {of, Subject, switchMap} from "rxjs"
import {v4 as uuid4} from "uuid"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-task-types-table",
    templateUrl: "./task-types-table.component.html",
    styleUrls: ["./task-types-table.component.scss"],
    imports: [
        MatTableModule,
        MatButtonModule,
        MatIconModule,
        TableCellComponent,
        OverflowableTextComponent,
        InfiniteScrollAnchorComponent,
        TypedMatCellDefDirective,
    ],
})
export class TaskTypesTableComponent extends BaseTableComponent<TaskTypesTableItemFragment> implements OnInit {
    readonly breakpoints = inject(BreakpointsService)
    readonly destroyRef = inject(DestroyRef)
    readonly dialog = inject(MatDialog)

    readonly itemsGql = inject(TaskTypesTableItemsGQL)
    readonly createTaskTypeGql = inject(TaskTypesTableCreateTaskTypeGQL)
    readonly deleteTaskTypeGql = inject(TaskTypesTableDeleteTaskTypeGQL)
    readonly updateTaskTypeGql = inject(TaskTypesTableUpdateTaskTypeGQL)

    readonly $columns = computed(() => ["taskType", "queueGroup", "defaultTimeout", "maxTimeout", "cancelTimeout", "invocationMethod", "endpoint"])

    loadMore$ = new Subject<boolean>()

    readonly $data = toSignal(
        this.dataLoader.batchedData$<TaskTypesTableItemFragment>(
            this.refresh.observeAllContentTypeModel$(ContentTypeModel.JobTask).pipe(
                switchMap(() =>
                    of((skip: number, take: number) => {
                        return fetchThrowingErrors(this.itemsGql)({skip, take}).then(({items, itemsCount}) => ({
                            items: items?.filter(Boolean) || [],
                            totalCount: itemsCount || 0,
                        }))
                    }),
                ),
            ),
            keepLoadingMore(this.loadMore$),
        ),
        {
            requireSync: true,
        },
    )

    ngOnInit() {
        // subscribe to updates from the refresh service
        this.refresh.itemSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((_item) => this.refresh.contentTypeModel(ContentTypeModel.JobTask))
    }

    private showEditDialog(item: TaskTypesTableItemFragment, edit = false): void {
        const dialogRef = this.dialog.open<EditTaskTypeDialogComponent, EditTaskTypeDialogData, EditTaskTypeDialogData>(EditTaskTypeDialogComponent, {
            width: "500px",
            data: {
                taskTypeInfo: item,
                edit,
            },
        })
        dialogRef.afterClosed().subscribe(async (result: EditTaskTypeDialogData | undefined) => {
            // Navigate back to task types page
            await this.router.navigate(["/task-types"], {queryParamsHandling: "preserve"})

            const info = result?.taskTypeInfo
            if (!info?.taskType) return

            if (result?.delete) {
                await this.notification.withUserFeedback(
                    async () => {
                        await mutateThrowingErrors(this.deleteTaskTypeGql)({id: info.id})
                        this.refresh.contentTypeModel(ContentTypeModel.JobTask)
                    },
                    {success: "Task type deleted.", error: "Task type could not be deleted."},
                )
            } else if (edit) {
                await this.notification.withUserFeedback(
                    async () => {
                        await mutateThrowingErrors(this.updateTaskTypeGql)({
                            input: {
                                id: info.id,
                                taskType: info.taskType,
                                cancelTimeout: info.cancelTimeout,
                                invocationMethod: info.invocationMethod,
                                endpoint: info.endpoint,
                                defaultTimeout: info.defaultTimeout,
                                maxTimeout: info.maxTimeout,
                                queueGroup: info.queueGroup,
                            },
                        })
                        this.refresh.contentTypeModel(ContentTypeModel.JobTask)
                    },
                    {success: "Task type updated.", error: "Task type could not be updated."},
                )
            } else {
                // Create new task type
                await this.notification.withUserFeedback(
                    async () => {
                        await mutateThrowingErrors(this.createTaskTypeGql)({
                            input: {
                                taskType: info.taskType,
                                cancelTimeout: info.cancelTimeout,
                                invocationMethod: info.invocationMethod,
                                endpoint: info.endpoint,
                                defaultTimeout: info.defaultTimeout,
                                maxTimeout: info.maxTimeout,
                                queueGroup: info.queueGroup,
                            },
                        })
                        this.refresh.contentTypeModel(ContentTypeModel.JobTask)
                    },
                    {success: "Task type created.", error: "Task type could not be created."},
                )
            }
        })
    }

    rowClick(row: TaskTypesTableItemFragment): void {
        this.showEditDialog({...row}, true)
    }

    openCreateDialog() {
        this.showEditDialog(
            {
                __typename: "JobTaskType",
                id: uuid4(),
                taskType: undefined!,
                queueGroup: undefined!,
                endpoint: undefined,
                defaultTimeout: undefined,
                invocationMethod: InvocationMethod.None,
                maxTimeout: undefined,
                cancelTimeout: undefined,
            },
            false,
        )
    }

    formatInvocationTypeForDisplay(value?: InvocationMethod) {
        switch (value) {
            case InvocationMethod.ComputeEngine:
                return "Compute Engine"
            case InvocationMethod.JobsV2:
                return "Jobs V2"
            case InvocationMethod.Http:
                return "HTTP"
            case InvocationMethod.CloudTasks:
                return "Cloud Tasks"
            case InvocationMethod.None:
                return "Pull"
            default:
                return value
        }
    }
}
